import React, { useState, useContext, useEffect, useCallback } from 'react';
import ProductContext from './ProductContext';
import productMasterModel from '../../model/product/ProductMaster.model';
import { useLoading } from '../../utils/LoadingUtil';
import validateForm from '../../utils/validateForm';
import { useNavigate } from 'react-router-dom';
import {
  getAllProducts,
  getProductsByPage as getProductsByPageService,
  getProductById as getProductByIdService,
  createProduct,
  updateProduct as updateProductService,
  deleteProduct as deleteProductService,
  uploadProductFile,
  uploadPromoPriceFile,
  uploadProductCondtitionFile,
  uploadProductNewCode,
  uploadProductImage,
} from '../../services/ProductService';
import { showAlert, ConfirmAlert } from '../../components'; // Import showAlert
import { handleApiError } from '@/lib/handleApiError';

/**
 * Provides product-related state and actions to its children via React Context.
 *
 * This context provider manages product master data, including CRUD operations,
 * pagination, searching, sorting, file uploads, and form validation.
 *
 * @component
 * @param {Object} props
 * @param {React.ReactNode} props.children - The child components that will have access to the product context.
 *
 * @context
 * @property {Object} productMaster - The current product master form state.
 * @property {Function} setProductMaster - Setter for product master state.
 * @property {Object} error - Validation or server error messages.
 * @property {Function} setError - Setter for error state.
 * @property {Function} getAllProduct - Fetches all products with optional query.
 * @property {Function} updateProduct - Updates a product by ID.
 * @property {Function} getProductById - Fetches a product by ID.
 * @property {Function} createProductMaster - Creates a new product.
 * @property {Array} productResponse - List of products for the current page.
 * @property {string} searchQuery - Current search query.
 * @property {Function} setSearchQuery - Setter for search query (resets page).
 * @property {Function} setCurrentPage - Setter for current page.
 * @property {Function} setPageSize - Setter for page size.
 * @property {number} totalElements - Total number of products.
 * @property {Function} setTotalElements - Setter for total elements.
 * @property {number} currentPage - Current page number.
 * @property {number} pageSize - Number of products per page.
 * @property {Function} clearFormData - Resets form data and errors.
 * @property {Function} deleteProduct - Deletes a product by ID.
 * @property {Function} getProductsByPage - Fetches products for a specific page.
 * @property {Function} startLoading - Sets loading state to true.
 * @property {Function} stopLoading - Sets loading state to false.
 * @property {boolean} isLoading - Indicates if a loading operation is in progress.
 * @property {Function} uploadProductFile - Handles product file upload.
 * @property {Function} uploadPromoPriceFile - Handles promo price file upload.
 * @property {Function} uploadProductCondtitionFile - Handles product condition file upload.
 * @property {Function} uploadProductNewCode - Handles new product code file upload.
 * @property {string} sortBy - Field to sort by.
 * @property {Function} setSortBy - Setter for sortBy.
 * @property {string} sortDir - Sort direction ('asc' or 'desc').
 * @property {Function} setSortDir - Setter for sortDir.
 *
 * @example
 * <ProductContextProvider>
 *   <YourComponent />
 * </ProductContextProvider>
 */
const ProductContextProvider = ({ children }) => {
  const { productMaster, setProductMaster, initialState } =
    productMasterModel();
  const { isLoading, startLoading, stopLoading } = useLoading();
  const [totalElements, setTotalElements] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [error, setError] = useState({});
  const [productResponse, setProductResponse] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const nav = useNavigate();
  const [sortBy, setSortBy] = useState('');
  const [sortDir, setSortDir] = useState('desc');
  const [proposalProducts, setProposalProducts] = useState([]);

  useEffect(() => {
    setCurrentPage(0);
    setSearchQuery('');
    setPageSize(50);
    setSortBy('');
    setSortDir('desc');
  }, [nav]);

  const isValidData = (productMaster) => {
    // Define error messages for basic fields
    const errorMessages = {
      itemCode: 'Product Code is required',
      itemDescription: 'Product Description is required',
      unit: 'Unit is required',
      productGroup: 'Product Group is required', // Added for nested field validation
      avgCost: 'Avg Cost is required',
    };
    if (productMaster.isBtsProduct) {
      errorMessages.btsPrice = 'BTS Price is required';
      errorMessages.normalPrice = 'Cost is required';
      errorMessages.btsConversionFactor = 'Conversion factor is required';
    }
    // Create an object to accumulate validation errors
    const validationErrors = {};

    // Perform basic validation
    const isValid = validateForm(
      productMaster,
      (errors) => {
        Object.assign(validationErrors, errors);
      },
      errorMessages,
    );

    // Additional checks for nested fields
    if (!productMaster.productGroup?.id) {
      validationErrors.productGroup = errorMessages.productGroup;
    }

    if (productMaster.avgCost === '' || productMaster.avgCost === null) {
      validationErrors.avgCost = 'Average Cost is required';
    }

    if (productMaster.isBtsProduct) {
      if (!productMaster.btsPrice) {
        validationErrors.btsPrice = errorMessages.btsPrice;
      }
      if (!productMaster.normalPrice) {
        validationErrors.normalPrice = errorMessages.normalPrice;
      }
      if (!productMaster.btsConversionFactor) {
        validationErrors.btsConversionFactor =
          errorMessages.btsConversionFactor;
      }
    }

    // Set all accumulated errors and stop loading if there are any errors
    if (Object.keys(validationErrors).length > 0) {
      setError(validationErrors);
      stopLoading();
      return 'error';
    }

    // If all validations pass
    return 'success'; // or true, depending on your use case
  };

  const createProductMaster = async (
    productMaster,
    file,
    e,
    closeModal,
    isCreatedFromProposal = false,
  ) => {
    e.preventDefault();
    const checkValidation = isValidData(productMaster);
    if (checkValidation === 'error') return;
    startLoading();
    try {
      const res = await createProduct(productMaster);
      if (file) {
        await uploadProductImage(res.id, file);
      }
      getProductsByPage();
      closeModal();
      clearFormData();
      if (isCreatedFromProposal) {
        setProposalProducts([res]);
      }
      showAlert('success', 'Product created successfully');
    } catch (error) {
      setError(error?.response?.data || 'An error occurred');
      handleApiError('error', error?.response?.data, error?.response?.status);
    } finally {
      stopLoading();
    }
  };
  const getAllProduct = async (query) => {
    try {
      const response = await getAllProducts(query);
      setTotalElements(response?.page?.totalElements || 0);
      setProposalProducts(response?._embedded?.productMasterDTOList || []);
      return response?._embedded?.productMasterDTOList || [];
    } catch (error) {
      setProductResponse([]); // Set blank array if there's an error
      setTotalElements(0); // Reset total elements to 0
      setError(error.response?.data || 'An error occurred');
      handleApiError('error', error.response?.data, error?.response?.status);
    }
  };

  const getProductsByPage = useCallback(
    async (
      page = currentPage,
      size = pageSize,
      search = searchQuery,
      sortField = sortBy, // Renamed parameter to avoid shadowing
      sortDirection = sortDir, // Renamed parameter to avoid shadowing
    ) => {
      startLoading();
      try {
        const response = await getProductsByPageService(
          page,
          size,
          search,
          sortField, // Use renamed parameter
          sortDirection, // Use renamed parameter
        );
        setProductResponse(
          response?._embedded?.productMasterDTOList || response,
        );
        setTotalElements(response?.page?.totalElements || 0);
        // return response;
      } catch (error) {
        setTotalElements(0);
        setProductResponse([]);
        setError(error.response?.data || 'An error occurred');
        handleApiError('error', error.response?.data, error?.response?.status);
      } finally {
        stopLoading();
      }
    },
    [currentPage, pageSize, searchQuery, createProductMaster],
  );

  const getProductById = async (id) => {
    try {
      const response = await getProductByIdService(id);
      return response;
    } catch (error) {
      setError(error.response?.data || 'An error occurred');
      handleApiError('error', error.response?.data, error?.response?.status);
    }
  };

  const updateProduct = async (productMaster, file,e,closeModal, id) => {
    e.preventDefault();
    startLoading();
    const isValid = isValidData(productMaster);
    if (isValid === 'error') return;
    try {
      await updateProductService(id, productMaster);
       if (file) {
        await uploadProductImage(id, file);
      }
      closeModal();
      getProductsByPage();
      clearFormData();
      showAlert('success', 'Product updated successfully');
    } catch (error) {
      setError(error.response?.data || 'An error occurred');
      handleApiError('error', error.response?.data, error?.response?.status);
    } finally {
      stopLoading();
    }
  };

  const deleteProduct = async (id) => {
    const result = await ConfirmAlert({
      confirmBtnText: 'Delete Product ',
    });
    if (!result.isConfirmed) {
      stopLoading();
      handleApiError('error', 'Product cancelled', error?.response?.status);
      return;
    }
    startLoading();
    try {
      await deleteProductService(id);
      getProductsByPage();
      showAlert('success', 'Product deleted successfully');
    } catch (error) {
      setError(error.response.data || 'An error occurred');
      handleApiError('error', error.response.data, error?.response?.status);
    } finally {
      stopLoading();
    }
  };

  const clearFormData = () => {
    setProductMaster({ ...initialState });
    setError({});
  };

  const handleSearchQueryChange = (query) => {
    setSearchQuery(query);
    setCurrentPage(0); // Reset to 0th page whenever search query changes
  };

  const updateProductImage = async (id, file) => {
    startLoading();
    try {
      await uploadProductImage(id, file);
      getProductsByPage();
      showAlert('success', 'Product image updated successfully');
    } catch (error) {
      setError(error.response?.data || 'An error occurred');
      handleApiError('error', error.response?.data, error?.response?.status);
    } finally {
      stopLoading();
    }
  };

  const handleProductFileUpload = async (file, config = {}) => {
    startLoading();
    try {
      const response = await uploadProductFile(file, config);
      showAlert('success', response || 'Product file uploaded successfully');
      return response;
    } catch (error) {
      setError(error.response?.data || 'An error occurred');
      handleApiError('error', error.response?.data, error?.response?.status);
    } finally {
      stopLoading();
    }
  };

  const handlePromoPriceFileUpload = async (file, config = {}) => {
    startLoading();
    try {
      const response = await uploadPromoPriceFile(file, config);
      showAlert(
        'success',
        response || 'Promo price file uploaded successfully',
      );
      return response;
    } catch (error) {
      setError(error.response?.data || 'An error occurred');
      handleApiError('error', error.response?.data, error?.response?.status);
    } finally {
      stopLoading();
    }
  };

  const handleProductConditionFileUpload = async (file, config = {}) => {
    startLoading();
    try {
      const response = await uploadProductCondtitionFile(file, config);
      showAlert(
        'success',
        response || 'Product condition file uploaded successfully',
      );
      return response;
    } catch (error) {
      setError(error.response?.data || 'An error occurred');
      handleApiError('error', error.response?.data, error?.response?.status);
    } finally {
      stopLoading();
    }
  };

  const handleProductNewCodeFileUpload = async (file, config = {}) => {
    startLoading();
    try {
      const response = await uploadProductNewCode(file, config);
      showAlert(
        'success',
        response || 'New product code file uploaded successfully',
      );
      return response;
    } catch (error) {
      setError(error.response?.data || 'An error occurred');
      handleApiError('error', error.response?.data, error?.response?.status);
    } finally {
      stopLoading();
    }
  };

  return (
    <ProductContext.Provider
      value={{
        productMaster,
        setProductMaster,
        error,
        setError,
        getAllProduct,
        updateProduct,
        getProductById,
        createProductMaster,
        productResponse,
        searchQuery,
        setSearchQuery: handleSearchQueryChange,
        setCurrentPage,
        setPageSize,
        totalElements,
        setTotalElements,
        currentPage,
        pageSize,
        clearFormData,
        deleteProduct,
        getProductsByPage,
        startLoading,
        stopLoading,
        isLoading,
        uploadProductFile: handleProductFileUpload,
        uploadPromoPriceFile: handlePromoPriceFileUpload,
        uploadProductCondtitionFile: handleProductConditionFileUpload,
        uploadProductNewCode: handleProductNewCodeFileUpload,
        updateProductImage, // Added updateProductImage to context
        sortBy,
        setSortBy,
        sortDir,
        setSortDir,
        proposalProducts,
        setProposalProducts,
      }}>
      {children}
    </ProductContext.Provider>
  );
};

export default ProductContextProvider;
export const useProduct = () => useContext(ProductContext);
